import { useState, useEffect } from "react";
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Check, Close } from "@mui/icons-material";

// Components
import Header from "./Header";
import { useNotification } from "../utils/Notification";
import { trackEvent } from "../utils/Utils";
import Plans from "../modals/Plans";

// Supabase
import supabase from "../../supabaseClient";

// Localize
import { useTranslation } from "react-i18next";

const Subscription = () => {
  const [teamData, setTeamData] = useState(null);
  const [openPlans, setOpenPlans] = useState(false);
  const [stripePortal, setStripePortal] = useState("");
  const { t } = useTranslation();
  const theme = useTheme();

  // Context
  const { showNotification } = useNotification();

  useEffect(() => {
    const fetchData = async () => {
      const { data: teamData, error: teamError } = await supabase
        .from("teams")
        .select(
          `
          plan(
            *
          ),
          messages_count,
          stripe_id
        `
        )
        .single();

      if (teamError) {
        showNotification(
          `${t("subscription.fetchError")}: ${teamError.message}`,
          "error"
        );
        return;
      }
      setTeamData(teamData);

      // Fetch stripe portal url
      const body = {
        customerId: teamData.stripe_id,
        returnUrl: `${window.location.origin}/dashboard/subscription`,
      };

      const stripeResponse = await fetch(
        "https://stripecustomerportal-tiph5o4x3q-uc.a.run.app",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );

      const stripeData = await stripeResponse.json();

      setStripePortal(stripeData.url);
    };

    fetchData();
  }, []);

  if (!teamData) {
    return <div>{t("subscription.loading")}</div>;
  }

  const planFeatures = [
    { label: t("subscription.contactsLimit"), value: "∞", type: "number" },
    {
      label: t("subscription.messagesMonthlyLimit"),
      value:
        teamData.plan.name === "Free"
          ? `${teamData.plan.messages_monthly_limit} ${t(
            "subscription.withKoubiLabel"
          )}`
          : teamData.plan.messages_monthly_limit === 1000000
            ? "∞"
            : teamData.plan.messages_monthly_limit,
      type: "number",
    },
    {
      label: t("subscription.templatesLimit"),
      value:
        teamData.plan.templates_limit === 1000000
          ? "∞"
          : teamData.plan.templates_limit,
      type: "number",
    },
    {
      label: t("subscription.filesLimit"),
      value: teamData.plan.attachments_limit,
      type: "number",
    },
    { label: t("subscription.variablesInMessages"), value: true },
    { label: t("subscription.labelsInMessages"), value: true },
    { label: t("subscription.selfReminders"), value: true },
    {
      label: t("subscription.importContacts"),
      value: teamData.plan.import_contacts_csv,
    },
    {
      label: t("subscription.importContactsWhatsApp"),
      value: teamData.plan.import_contacts_whatsapp,
    },
    {
      label: t("subscription.automaticBirthdayMessages"),
      value: teamData.plan.birthday_messages,
    },
    {
      label: t("subscription.groupBulkMessages"),
      value: teamData.plan.group_messages,
    },
    {
      label: t("subscription.recurringMessages"),
      value: teamData.plan.recurring_messages,
    },
    { label: t("subscription.koubiApi"), value: teamData.plan.api },
    {
      label: t("subscription.premiumSupport"),
      value: teamData.plan.premium_support,
    },
  ];

  const handleOpenPlans = () => {
    trackEvent("subscription-upgrade_clicked");
    setOpenPlans(true);
  };
  const handleClosePlans = () => setOpenPlans(false);
  const handleManageSubscription = () => {
    if (!stripePortal) {
      showNotification(t("subscription.pageLoading"));
      return;
    }
    trackEvent("subscription-manage_clicked");
    window.open(stripePortal, "_blank");
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: "1200px", mx: "auto" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Header title={t("subscription.title")} subtitle={t("subscription.subtitle")} />
      </Box>

      {/* Subscription Summary Card */}
      <Paper
        elevation={0}
        sx={{
          p: 4,
          mb: 4,
          borderRadius: "16px",
          bgcolor: theme.palette.mode === 'dark' ? "rgba(20, 27, 45, 0.7)" : "#ffffff",
          backdropFilter: "blur(10px)",
          border: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
          boxShadow: theme.palette.mode === 'dark' ? "0 8px 32px 0 rgba(0, 0, 0, 0.3)" : "0 8px 32px 0 rgba(31, 38, 135, 0.05)",
        }}
      >
        <Box display="flex" flexDirection={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} gap={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" fontWeight="bold" textTransform="uppercase" letterSpacing={1.5} mb={1}>
              {t("subscription.plan")}
            </Typography>
            <Typography variant="h3" fontWeight="bold" sx={{ color: theme.palette.mode === 'dark' ? "#fff" : "#141b2d" }}>
              {teamData.plan.name}
            </Typography>
            <Box mt={2}>
              <Typography variant="body1" color="text.secondary" sx={{ display: "inline-block", mr: 1 }}>
                {t("subscription.monthlyMessageCount")}:
              </Typography>
              <Typography variant="body1" fontWeight="bold" sx={{ display: "inline-block", color: theme.palette.mode === 'dark' ? "#4cceac" : "#3da58a" }}>
                {teamData.messages_count} / {teamData.plan.messages_monthly_limit === 1000000 ? "∞" : teamData.plan.messages_monthly_limit}
              </Typography>
            </Box>
          </Box>

          <Box display="flex" gap={2}>
            {teamData.plan.name !== "Premium" && (
              <Button
                onClick={handleOpenPlans}
                variant="contained"
                sx={{
                  borderRadius: "8px",
                  px: 3,
                  py: 1.5,
                  fontWeight: "bold",
                  background: "linear-gradient(90deg, #4cceac 0%, #3da58a 100%)",
                  color: "#141b2d",
                  boxShadow: "0 4px 14px 0 rgba(76, 206, 172, 0.39)",
                  "&:hover": { background: "linear-gradient(90deg, #3da58a 0%, #2e7c67 100%)", color: "#fff" }
                }}
              >
                {t("subscription.upgrade")}
              </Button>
            )}
            <Button
              onClick={handleManageSubscription}
              variant="contained"
              sx={{
                borderRadius: "8px",
                px: 3,
                py: 1.5,
                fontWeight: "bold",
                background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                color: "#fff",
                boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
              }}
            >
              {t("subscription.manage")}
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Included in your plan */}
      <Typography variant="h5" fontWeight="600" mb={3} sx={{ color: theme.palette.mode === 'dark' ? "#fff" : "#141b2d", mt: 6 }}>
        {t("subscription.feature", "Plan Features")}
      </Typography>

      <TableContainer
        component={Paper}
        elevation={0}
        sx={{
          borderRadius: "16px",
          bgcolor: theme.palette.mode === 'dark' ? "rgba(20, 27, 45, 0.7)" : "#ffffff",
          border: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
          overflow: "hidden"
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa" }}>
              <TableCell sx={{ fontWeight: "bold", borderBottom: "none", py: 2 }}>
                {t("subscription.feature")}
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold", borderBottom: "none", py: 2 }}>
                {t("subscription.yourPlan")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {planFeatures
              .sort((a, b) => b.value - a.value)
              .map((feature, index) => (
                <TableRow
                  key={feature.label}
                  sx={{
                    "&:last-child td, &:last-child th": { border: 0 },
                    "&:hover": { bgcolor: theme.palette.mode === 'dark' ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)" }
                  }}
                >
                  <TableCell sx={{ borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)" }}>
                    <Typography variant="body1" fontWeight={500}>{feature.label}</Typography>
                  </TableCell>
                  <TableCell align="center" sx={{ borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)" }}>
                    {feature.type === "number" ? (
                      <Typography variant="body1" fontWeight="bold" color="primary">{feature.value}</Typography>
                    ) : feature.value ? (
                      <Check sx={{ color: "#4cceac" }} />
                    ) : (
                      <Close sx={{ color: "#e2726e" }} />
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Plans open={openPlans} onClose={handleClosePlans} />
    </Box>
  );
};

export default Subscription;
